import { useSettings } from "@ext/enterprise/components/admin/contexts/SettingsContext";
import {
	type QuerySearchFn,
	useQuerySearch,
} from "@ext/enterprise/components/admin/settings/members/hooks/useQuerySearch";
import {
	emailKey,
	guestToUserMember,
	type MemberAggregate,
	mapSsoToUserMember,
	type UserMember,
} from "@ext/enterprise/components/admin/settings/members/model/Member";
import type { searchUserInfo } from "@ext/enterprise/EnterpriseService";
import { useCallback, useMemo, useState } from "react";

export type FilterTypeOption = "editor" | "owner" | "guest";

interface UseUsersListArgs {
	aggregate: MemberAggregate;
	enabled: boolean;
	guestsEnabled?: boolean;
	onUsersLoad?: (users: UserMember[]) => void;
}

export const useUserList = (args: UseUsersListArgs) => {
	const { aggregate, enabled, guestsEnabled, onUsersLoad } = args;
	const { ssoUsersEnabled: ssoEnabled, ssoUsersEagerList, searchUsers } = useSettings();

	const [query, setQuery] = useState("");

	const [selectedTypeFilter, setTypeFilter] = useState<FilterTypeOption | null>(null);
	// The guest filter has no meaning once one-time password sign-in is switched off
	const typeFilter = selectedTypeFilter === "guest" && !guestsEnabled ? null : selectedTypeFilter;

	const isTypeFilter = typeFilter != null;

	// External readers are listed locally, even when the sso directory knows their email
	const guestUsers = useMemo<UserMember[]>(() => {
		if (!guestsEnabled) return [];
		return aggregate.guests.filter((x) => !aggregate.usersByValue.has(emailKey(x.value))).map(guestToUserMember);
	}, [guestsEnabled, aggregate.guests, aggregate.usersByValue]);

	const guestsByEmail = useMemo(() => new Map(guestUsers.map((x) => [emailKey(x.value), x])), [guestUsers]);

	const mapSso = useCallback(
		(sso: searchUserInfo) => {
			return guestsByEmail.get(emailKey(sso.email)) ?? mapSsoToUserMember(sso, aggregate.usersByValue);
		},
		[aggregate.usersByValue, guestsByEmail],
	);

	const fetchUsers: QuerySearchFn<UserMember> = useCallback(
		async (query: string) => {
			const res = await searchUsers(query);
			return res.map(mapSso);
		},
		[searchUsers, mapSso],
	);

	// Has sso, no type filter, and either eager list is on or there's a query - sso search
	const isSsoSearch = enabled && ssoEnabled && !isTypeFilter && (ssoUsersEagerList || Boolean(query));

	const { data: searchedUsers, isLoading: searchLoading } = useQuerySearch({
		enabled: isSsoSearch,
		searchFn: fetchUsers,
		onLoad: onUsersLoad,
		query,
	});

	const filterPredicate = useCallback(
		(x: UserMember) => {
			const q = query.toLowerCase();
			return (
				(!query || x.value.toLowerCase().includes(q) || x.name?.toLowerCase().includes(q)) &&
				(typeFilter == null ||
					(typeFilter === "editor" && x.isEditor && !x.isGuest) ||
					(typeFilter === "owner" && x.isWorkspaceOwner && !x.isGuest) ||
					(typeFilter === "guest" && x.isGuest))
			);
		},
		[typeFilter, query],
	);

	// No sso - all local users, has sso - only local users known to sso
	const filteredLocalUsers = useMemo(() => {
		if (!enabled) return [];
		const res = [...aggregate.users, ...guestUsers].filter(filterPredicate);
		onUsersLoad?.(res);
		return res;
	}, [aggregate.users, guestUsers, filterPredicate, onUsersLoad, enabled]);

	const users = useMemo(() => {
		if (!isSsoSearch) return filteredLocalUsers;
		const ssoByEmail = new Map(searchedUsers.map((x) => [emailKey(x.value), x]));
		const local = filteredLocalUsers.map((x) => ssoByEmail.get(emailKey(x.value)) ?? x);
		local.forEach((x) => ssoByEmail.delete(emailKey(x.value)));
		return [...local, ...ssoByEmail.values()];
	}, [isSsoSearch, filteredLocalUsers, searchedUsers]);

	return {
		data: {
			users,
			isLoading: searchLoading,
		},
		filter: {
			query: {
				value: query,
				set: setQuery,
			},
			type: {
				selected: typeFilter,
				set: setTypeFilter,
			},
		},
	};
};
